import { createAuthStore } from "@/stores/createAuthStore";
import {
  physicianLoginApi,
  physicianRefreshApi,
  physicianLogoutApi,
} from "@/portals/physician/api/physicianAuthApi";
import type { ContractResult } from "@/portals/physician/api/contractErrors";
import type { PhysicianAuthResponse, PhysicianProfile } from "@/portals/physician/api/types";

// Its own createAuthStore instance registered under portal "physician": its own
// in-memory token, refresh timer, and apiClient binding, so it can never read or
// overwrite the patient/admin sessions (see physicianAuthStore.test.ts, F6).
// The user is read loosely from login/refresh because the contract doesn't name
// the key; PhysicianLayout fetches /api/physician/me for the displayed name anyway.
function toAuthApiResult({ data, error }: ContractResult<PhysicianAuthResponse>) {
  if (!data) return { data: null, error: error?.message ?? null, code: error?.code ?? null };
  return {
    data: {
      accessToken: data.accessToken,
      expiresIn: data.expiresIn ?? NaN,
      user: data.physician ?? data.user ?? null,
    },
    error: null,
  };
}

export const usePhysicianAuthStore = createAuthStore<PhysicianProfile | null, [email: string, password: string]>({
  portal: "physician",
  signIn: async (email, password) => {
    const result = await physicianLoginApi(email, password);
    // Contract: every login 401 is INVALID_CREDENTIALS, whatever code string rides along.
    if (result.error?.status === 401) result.error = { ...result.error, code: "INVALID_CREDENTIALS" };
    return toAuthApiResult(result);
  },
  refresh: async () => toAuthApiResult(await physicianRefreshApi()),
  signOut: () => physicianLogoutApi().then(() => {}),
});
